import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { extractHeadings, makeHighlighter } from "@/lib/helpMarkdown";

/**
 * In-document search: highlighter for rendering, <mark> navigation and
 * heading hits. `container` is the mounted DOM node of the active document;
 * `contentKey` changes whenever that document's rendered content changes.
 */
export function useDocSearch(container, content, contentKey) {
  const [query, setQuery] = useState("");
  const [currentMatch, setCurrentMatch] = useState(0);
  const [matchCount, setMatchCount] = useState(0);
  const marksRef = useRef([]);

  const highlighter = useMemo(() => makeHighlighter(query), [query]);
  const headings = useMemo(() => extractHeadings(content), [content]);
  const matchingHeadings = useMemo(() => {
    const q = query.toLowerCase();
    return q ? headings.filter((h) => h.text.toLowerCase().includes(q)) : [];
  }, [headings, query]);

  // Re-index <mark> nodes after the highlighted content has been committed.
  useEffect(() => {
    marksRef.current = container ? Array.from(container.querySelectorAll("mark.help-hl")) : [];
    setMatchCount(marksRef.current.length);
    setCurrentMatch(0);
  }, [container, query, contentKey]);

  const jumpToMatch = useCallback((index) => {
    const marks = marksRef.current;
    if (!marks.length) return;
    const next = ((index % marks.length) + marks.length) % marks.length;
    setCurrentMatch(next);
    marks.forEach((el, i) => el.classList.toggle("help-hl-active", i === next));
    marks[next]?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, []);

  const scrollToHeading = useCallback((slug) => {
    container?.querySelector(`[data-heading-slug="${slug}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [container]);

  const clear = useCallback(() => { setQuery(""); setCurrentMatch(0); }, []);

  return {
    query, setQuery, clear, highlighter, matchingHeadings,
    currentMatch, matchCount, jumpToMatch, scrollToHeading,
  };
}
